// Hello Mortgage Insights — LLM-generated analyst blurb shown at the top
// of public pages (homepage Rate Intelligence, per-term detail, /compare-all).
// Matches the Ratehub "yellow highlight box" treatment but branded for Hello
// Mortgage. Renders nothing when the backend returns an empty body (graceful
// degradation when the LLM can't be called).
import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { publicApi } from "./api";

export default function InsightsBlurb({ context = "general", onCta, variant = "light" }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await publicApi.get(`/insights/${context}`);
        if (alive) setData(r.data);
      } catch {
        /* swallow — blurb is nice-to-have, never blocks the page */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [context]);

  // Graceful hide — empty body means no LLM output (no key, API fail, etc.)
  if (!loaded || !data || !data.body) return null;

  const updated = data.updated_at
    ? new Date(data.updated_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
    : null;

  // Two visual treatments: "light" (warm cream, for light-bg pages) and
  // "dark" (charcoal-on-navy, for the RateIntelligence dark section).
  const palette = variant === "hello"
    ? { wrap: "rt-insight", body: "rt-insight-body", cta: "rt-insight-cta", meta: "rt-insight-meta" }
    : variant === "dark"
    ? {
        wrap: "bg-amber-50/5 border-amber-400/30 backdrop-blur-sm",
        body: "text-white/90",
        cta: "text-amber-300 hover:text-amber-200",
        meta: "text-white/40",
      }
    : {
        wrap: "bg-amber-50 border-amber-200",
        body: "text-slate-800",
        cta: "text-brand-red hover:text-brand-red-hover",
        meta: "text-slate-500",
      };

  return (
    <div
      className={`relative border rounded-2xl p-5 sm:p-6 ${palette.wrap}`}
      data-testid={`insights-blurb-${context}`}
    >
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-[0.18em] font-body ${variant === "dark" ? "text-amber-300" : "text-brand-red"}`}>
          Hello Mortgage Insights
        </span>
        {updated && (
          <span className={`text-[11px] font-body ${palette.meta}`}>
            <span aria-hidden="true" className="opacity-50">{" · "}</span>
            Updated {updated}
          </span>
        )}
      </div>
      <p
        className={`text-sm sm:text-base font-body leading-relaxed ${palette.body}`}
        data-testid={`insights-body-${context}`}
      >
        {data.body}
      </p>
      {data.cta && onCta && (
        <button
          type="button"
          onClick={onCta}
          data-testid={`insights-cta-${context}`}
          className={`mt-3 inline-flex items-center gap-1.5 text-sm font-bold font-body transition-colors ${palette.cta}`}
        >
          {data.cta}
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
